import * as s from "styles/ModalCommonStyled";
import BigPopupTemplate from "components/templates/modal/big/BigPopupTemplate";
import BigButton from "components/atoms/buttons/BigButton";
import { ModalInput } from "components/atoms/input/modal/ModalInput";
import Input from "components/atoms/input/Input";
import { Text } from "components/atoms/text/Text";
import { ValidateMsg } from "api/interfaces/commonInterface.interface";
import { UnprivilegedEditor } from "react-quill";
import { DeltaStatic, Sources } from "quill";
import SelectComponent from "components/atoms/select/SelectComponent";
import ToggleComponent from "components/atoms/toggle/Toggle";
import MapUpload from "components/atoms/input/upload/map/MapUpload";
import { covaModelOptions, firwareAvailableTypeOptions } from "utils/options";
import { LuFileBox } from "react-icons/lu";
import * as fs from "../FirmwareDWStyled";
import {
  CreateFirmwarePackageRequest,
  FirmwarePackageListResult,
} from "api/interfaces/settingsInterface.interface";
import DeactivateButton from "components/blocks/modal/deactivate";
import Spinner from "components/atoms/loader/Spinner";
import EmailEditor from "components/atoms/input/editor/EmailEditor";

interface Props {
  onModal: (type?: string) => void;
  onChange: (e: any) => void;
  form: CreateFirmwarePackageRequest;
  onDelete?: (id: string) => void;
  validateMsg?: ValidateMsg;
  isPermanently: boolean;
  onChangePermanent: (e: any) => void;
  onSave: () => void;
  onUpload: (e: File) => void;
  onDownloadFile: (id: string) => void;
  selectedRow?: FirmwarePackageListResult | undefined;
  isLoading: boolean;
  uploadFormRef: React.RefObject<HTMLInputElement>;
}

export function AddModalPresenter(props: Props): JSX.Element {
  return (
    <BigPopupTemplate
      title={
        props.selectedRow ? "Edit Firmware Package" : "Add Firmware Package"
      }
      onModal={props.onModal}
    >
      <s.AddWrapper>
        <s.Row>
          <ModalInput
            label="Recorder Type"
            essential
            flex={1}
            validatedWord={props.validateMsg?.recorderType}
            isValidated={!!props.validateMsg?.recorderType}
          >
            <SelectComponent
              options={firwareAvailableTypeOptions}
              value={firwareAvailableTypeOptions[0]}
            />
          </ModalInput>
          <ModalInput
            label="Version"
            essential
            flex={1}
            validatedWord={props.validateMsg?.version}
            isValidated={!!props.validateMsg?.version}
          >
            <Input
              placeholder="Enter a version number"
              name="version"
              onChange={props.onChange}
              value={props.form.version}
              autoFocus={true}
              onKeyDown={(e) => {
                if (e.ctrlKey || e.metaKey || e.altKey) return;
                if (e.key.length !== 1) return;
                if (!/^[\d.-]$/.test(e.key)) {
                  e.preventDefault();
                }
              }}
              maxLength={40}
              data-testid="maintenance-firmware-add-version-change"
            />
          </ModalInput>
        </s.Row>

        <ModalInput
          label="Model"
          essential
          validatedWord={props.validateMsg?.model}
          isValidated={!!props.validateMsg?.model}
        >
          <s.RowModalInput>
            {covaModelOptions.map((model, idx) => (
              <s.CheckInput key={idx}>
                <input
                  type="radio"
                  name="model"
                  value={model.value}
                  onChange={props.onChange}
                  checked={model.value === props.form.model}
                  data-testid={`maintenance-firmware-add-model-radio-${model.value}`}
                />

                <Text fontSize={14}>{model.label}</Text>
              </s.CheckInput>
            ))}
          </s.RowModalInput>
        </ModalInput>
        <ModalInput
          label="Upload Firmware Package"
          essential
          validatedWord={props.validateMsg?.file}
          isValidated={!!props.validateMsg?.file}
        >
          <MapUpload
            accept="application/pkg"
            onUpload={props.onUpload}
            ref={props.uploadFormRef}
          />

          {props.selectedRow?.id && (
            <fs.FileWrapper
              onClick={() => props.onDownloadFile(props.selectedRow?.id ?? "")}
              data-testid="maintenance-firmware-add-file-wrapper-download-file"
            >
              <LuFileBox size={14} />
              {props.selectedRow.fileName}
            </fs.FileWrapper>
          )}
        </ModalInput>

        <ModalInput
          label="Release Note"
          essential
          validatedWord={props.validateMsg?.releaseNote}
          isValidated={!!props.validateMsg?.releaseNote}
        >
          <EmailEditor
            value={props.form.releaseNote}
            onChange={(
              value: string,
              delta: DeltaStatic,
              source: Sources,
              editor: UnprivilegedEditor
            ) => {
              props.onChange({ target: { name: "releaseNote", value } });
            }}
          />
        </ModalInput>

        <ModalInput label="Active" essential>
          <ToggleComponent
            isToggled={props.form.isActive}
            checked={props.form.isActive}
            onChange={(e) =>
              props.onChange({ target: { name: "isActive", value: e } })
            }
            dataTestId="maintenance-firmware-add-active-toggle"
          />
        </ModalInput>
        <s.ButtonWrapper>
          {props.selectedRow && props.onDelete && (
            <DeactivateButton
              title="Do you really want to delete this firmware package?"
              subTitle="It cannot be recovered."
              onDeactive={() => props.onDelete?.(props.selectedRow?.id ?? "")}
              data-testid="maintenance-firmware-add-deactivate-button"
            />
          )}

          <BigButton
            label="Save"
            onClickBtn={props.onSave}
            data-testid="maintenance-firmware-add-button-save"
          />
        </s.ButtonWrapper>
      </s.AddWrapper>
      {props.isLoading && <Spinner />}
    </BigPopupTemplate>
  );
}
